/**
 * Landing Page Component
 * 
 * Public page for unauthenticated users
 * - Provides prominent login button
 * - Redirects authenticated users to dashboard
 * 
 * Requirements: 1.1, 1.2
 */

import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, login } = useAuth();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  /**
   * Handle login button click
   * Requirements: 1.2
   */
  const handleLogin = useCallback(() => {
    login();
  }, [login]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-primary-700">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
      {/* Header */}
      <header className="bg-primary-500 text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-3xl font-bold">Blue Clipboard</h1>
          <p className="text-primary-100 mt-1">Cycling Event Management</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 flex-grow flex items-center justify-center">
        <div className="max-w-2xl mx-auto">
          {/* Login Button */}
          <div className="text-center">
            <button
              onClick={handleLogin}
              className="bg-primary-500 hover:bg-primary-600 text-white font-bold py-4 px-12 rounded-lg shadow-lg transform transition hover:scale-105 focus:outline-none focus:ring-4 focus:ring-primary-300"
              data-testid="login-button"
            >
              Log In to Blue Clipboard
            </button>
            <div className="mt-6">
              <button
                onClick={() => navigate('/recover')}
                className="text-sm font-medium text-primary-700 hover:text-primary-900 underline"
                data-testid="landing-recover-access-link"
              >
                Can't log in or never set a password? Recover access
              </button>
              <p className="mt-2 text-xs text-gray-500 max-w-md mx-auto">
                Use this if you were invited, had your password reset by an admin, or the login
                page's "Forgot password" isn't working for you.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-primary-800 text-primary-100 py-6">
        <div className="container mx-auto px-4 text-center">
          <p>&copy; {new Date().getFullYear()} Blue Clipboard. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
